import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { requestNotificationPermission } from '../../config/messaging';

const BRAND_COLORS = {
  primary: '#1a1a1a',
  accent: '#C9A961',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
};

const NotificationPrompt = ({ userId, userType, message, onClose }) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    // Check if user has already responded to notification prompt
    const hasResponded = localStorage.getItem(`notificationPrompt_${userId}`);
    const notificationPermission = Notification.permission;

    // Show prompt if user hasn't responded and permission is default
    if (!hasResponded && notificationPermission === 'default') {
      setShouldShow(true);
    }
  }, [userId]);

  const handleEnableNotifications = async () => {
    setIsRequesting(true);

    const collectionName = userType === 'employer' ? 'employers' : 'professionals';
    const result = await requestNotificationPermission(userId, collectionName);

    if (result.success) {
      localStorage.setItem(`notificationPrompt_${userId}`, 'accepted');
      setShouldShow(false);
      if (onClose) onClose(true);
    } else {
      localStorage.setItem(`notificationPrompt_${userId}`, 'denied');
      setShouldShow(false);
      if (onClose) onClose(false);
    }

    setIsRequesting(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(`notificationPrompt_${userId}`, 'dismissed');
    setShouldShow(false);
    if (onClose) onClose(false);
  };

  if (!shouldShow) return null;

  return (
    <div className="bg-white border-2 p-6 md:p-8 mb-8 relative" style={{ borderColor: BRAND_COLORS.primary }}>
      <button
        onClick={handleDismiss}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="flex items-start space-x-4 pr-8">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 border-2 rounded-full flex items-center justify-center" style={{ borderColor: BRAND_COLORS.accent }}>
            <Bell className="w-6 h-6" style={{ color: BRAND_COLORS.accent }} />
          </div>
        </div>

        <div className="flex-1">
          <h3 className="text-xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
            Enable Notifications
          </h3>
          <p className="text-gray-600 font-light leading-relaxed mb-4">
            {message}
          </p>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleEnableNotifications}
              disabled={isRequesting}
              className="px-6 py-2.5 border-2 font-light tracking-wide hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: BRAND_COLORS.primary,
                borderColor: BRAND_COLORS.primary,
                color: 'white'
              }}
            >
              {isRequesting ? 'Enabling...' : 'Enable Notifications'}
            </button>

            <button
              onClick={handleDismiss}
              className="px-6 py-2.5 border-2 font-light tracking-wide hover:bg-gray-50 transition-all"
              style={{
                borderColor: BRAND_COLORS.lightGray,
                color: BRAND_COLORS.primary
              }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
